import {
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  closeHseEvent,
  deleteHseEvent,
  exportHseEventPDF,
  submitHseEvent,
} from "../../../api/hse/events";
import { downloadHsePdfBlob, raiseHsePdfRequestError } from "../../../api/hse/downloadPdf";
import { HseRecordActionsMenu } from "./HseRecordActionsMenu";

const getEventTypeIcon = (eventType) => {
  switch (eventType) {
    case "injury":
      return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
    case "near_miss":
      return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    case "property_damage":
      return <XCircleIcon className="h-5 w-5 text-orange-500" />;
    case "environmental":
      return <ExclamationTriangleIcon className="h-5 w-5 text-green-500" />;
    default:
      return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
  }
};

const badgeBase =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

export function HseEventListRow({ event, onChanged }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const viewPath = `/dashboard/hse-ks/events/${event.id}`;

  const statusBadge = (() => {
    switch (event.status) {
      case "draft":
        return <span className={`${badgeBase} bg-gray-100 text-gray-800`}>{t("Kladd")}</span>;
      case "submitted":
        return <span className={`${badgeBase} bg-blue-100 text-blue-800`}>{t("Innsendt")}</span>;
      case "under_review":
        return (
          <span className={`${badgeBase} bg-yellow-100 text-yellow-800`}>
            {t("Under vurdering")}
          </span>
        );
      case "closed":
        return <span className={`${badgeBase} bg-green-100 text-green-800`}>{t("Lukket")}</span>;
      default:
        return <span className={`${badgeBase} bg-gray-100 text-gray-800`}>{event.status}</span>;
    }
  })();

  const severityBadge = (() => {
    switch (event.severity_level) {
      case "low":
        return <span className={`${badgeBase} bg-green-100 text-green-800`}>{t("Lav")}</span>;
      case "medium":
        return <span className={`${badgeBase} bg-yellow-100 text-yellow-800`}>{t("Medium")}</span>;
      case "high":
        return <span className={`${badgeBase} bg-orange-100 text-orange-800`}>{t("Høy")}</span>;
      case "critical":
        return <span className={`${badgeBase} bg-red-100 text-red-800`}>{t("Kritisk")}</span>;
      default:
        return null;
    }
  })();

  const handleExportPdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await exportHseEventPDF(event.id);
      await downloadHsePdfBlob(
        response.data,
        `hms_hendelse_${event.id}_${new Date().toISOString().split("T")[0]}.pdf`
      );
      toast.success(t("hse.actions.export_pdf_success"));
    } catch (error) {
      try {
        await raiseHsePdfRequestError(error);
      } catch (parsed) {
        toast.error(parsed.message || t("hse.actions.export_pdf_error"));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await submitHseEvent(event.id);
      toast.success(t("hse.events.submitted"));
      onChanged?.();
    } catch (err) {
      toast.error(t("hse.events.submit_error"));
    }
  };

  const handleClose = async () => {
    try {
      await closeHseEvent(event.id);
      toast.success(t("hse.events.closed"));
      onChanged?.();
    } catch (err) {
      toast.error(t("hse.events.close_error"));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("hse.events.delete_confirm"))) return;
    try {
      await deleteHseEvent(event.id);
      toast.success(t("hse.events.deleted"));
      onChanged?.();
    } catch (err) {
      toast.error(t("hse.events.delete_error"));
    }
  };

  return (
    <li
      className="cursor-pointer px-6 py-4 hover:bg-gray-50"
      onClick={() => navigate(viewPath)}
    >
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center space-x-3">
          <div className="flex-shrink-0">{getEventTypeIcon(event.event_type)}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2">
              <p className="truncate text-sm font-medium text-gray-900">{event.title}</p>
              {statusBadge}
              {severityBadge}
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>{event.event_type_display}</span>
              <span>
                {event.event_date ? new Date(event.event_date).toLocaleDateString() : ""}
              </span>
              <span>{event.location}</span>
            </div>
          </div>
        </div>
        <HseRecordActionsMenu
          items={[
            {
              key: "open",
              label: t("hse.actions.open"),
              icon: EyeIcon,
              onClick: () => navigate(viewPath),
            },
            {
              key: "edit",
              label: t("hse.actions.edit"),
              icon: PencilIcon,
              hidden: !event.can_be_edited,
              onClick: () => navigate(`${viewPath}/edit`),
            },
            {
              key: "export_pdf",
              label: busy ? t("hse.actions.export_pdf_loading") : t("hse.actions.export_pdf"),
              icon: DocumentArrowDownIcon,
              disabled: busy,
              onClick: handleExportPdf,
            },
            {
              key: "submit",
              label: t("hse.actions.submit"),
              icon: CheckCircleIcon,
              hidden: event.status !== "draft",
              onClick: handleSubmit,
            },
            {
              key: "close",
              label: t("hse.actions.close_event"),
              icon: CheckCircleIcon,
              hidden: event.status !== "submitted" && event.status !== "under_review",
              onClick: handleClose,
            },
            {
              key: "delete",
              label: t("hse.actions.delete"),
              icon: TrashIcon,
              danger: true,
              hidden: !event.can_be_edited,
              onClick: handleDelete,
            },
          ]}
        />
      </div>
    </li>
  );
}
